import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import history from '../../../history';
import { executeHttpRequest } from '../../../epics/api';
import { getErrorMessages } from '../../../utils';
import { TApiMeta } from '../../../types/api';
import {
    TFilterChangePayload,
    TFilterOptions,
    TFilterOptionsToDisplay,
    TGlobalFreelancersProps,
    TGlobalWorker,
} from './types';
import { PAGE_SIZE } from './constants';

export const useGlobalFreelancers = ({
    notification,
}: TGlobalFreelancersProps) => {
    const location = useLocation();
    const [content, setContent] = useState<TGlobalWorker[]>([]);
    const [meta, setMeta] = useState<TApiMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [filterOptions, setFilterOptions] = useState<TFilterOptions | null>(null);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
    const didTouchDefaultFilters = useRef(false);

    const filterOptionValuesToDisplay = useMemo<TFilterOptionsToDisplay>(
        () => ({
            serviceKey: [
                { label: 'All services', value: '' },
                ...(filterOptions?.service_key || []),
            ],
            interviewStatus: [
                { label: 'All interview statuses', value: '' },
                ...(filterOptions?.interview_status || []),
            ],
            isPublished: [
                { label: 'All publication statuses', value: '' },
                ...(filterOptions?.is_published || []).map(option => ({
                    value: String(option.value),
                    label: option.label,
                })),
            ],
            isArchived: [
                { label: 'All archive statuses', value: '' },
                ...(filterOptions?.is_archived || []).map(option => ({
                    value: String(option.value),
                    label: option.label,
                })),
            ],
            isActive: [
                { label: 'All active statuses', value: '' },
                ...(filterOptions?.is_active || []).map(option => ({
                    value: String(option.value),
                    label: option.label,
                })),
            ],
        }),
        [filterOptions],
    );

    const allowedSortColumns = useMemo(
        () =>
            new Set(
                (filterOptions?.ordering || []).map(sortKey =>
                    sortKey.replace(/^-/, ''),
                ),
            ),
        [filterOptions],
    );

    const fetchFilterOptions = useCallback(() => {
        executeHttpRequest('global-freelancers/filter-options/', 'get')
            .then(response => {
                setFilterOptions(response?.data?.content || null);
            })
            .catch(err => {
                const message = getErrorMessages(err);
                notification.error('Error', message);
            })
            .finally(() => setFilterOptionsLoaded(true));
    }, [notification]);

    const pushFiltersToParamter = useCallback(
        (filters: Record<string, string>) => {
            const currentUrlParams = new URLSearchParams(location.search);
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    currentUrlParams.set(key, filters[key]);
                } else {
                    currentUrlParams.delete(key);
                }
            });

            currentUrlParams.set('page', '1');
            history.push(`${location.pathname}?${currentUrlParams.toString()}`);
            setCurrentPage(0);
        },
        [location.pathname, location.search],
    );

    const onFilterChange = useCallback(
        (id: string, value: TFilterChangePayload | string | null) => {
            if (
                id === 'is_published' ||
                id === 'is_archived' ||
                id === 'is_active' ||
                id === 'service_key' ||
                id === 'interview_status'
            ) {
                didTouchDefaultFilters.current = true;
            }

            if (value && typeof value === 'object' && 'value' in value) {
                const filter: Record<string, string> = {};
                filter[id] = String(value.value);
                pushFiltersToParamter(filter);
            } else if (value != null) {
                pushFiltersToParamter({ [id]: String(value) });
            }
        },
        [pushFiltersToParamter],
    );

    const fetchWorkers = useCallback(
        (
            pageIndex: number,
            searchParams: URLSearchParams,
            signal?: AbortSignal,
        ) => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(pageIndex + 1));
            params.set('paginate_by', String(PAGE_SIZE));

            setLoading(true);

            return executeHttpRequest(
                `global-freelancers/?${params.toString()}`,
                'get',
                null,
                true,
                { signal },
            )
                .then(response => {
                    setContent(response.data?.content || []);
                    setMeta(response.data?.meta || null);
                })
                .catch(err => {
                    if (err?.code === 'ERR_CANCELED') {
                        return;
                    }
                    const message = getErrorMessages(err);
                    notification.error('Error', message);
                })
                .finally(() => setLoading(false));
        },
        [notification],
    );

    const applyDefaultFilters = useCallback(() => {
        if (didTouchDefaultFilters.current || !filterOptions?.defaults) {
            return;
        }

        const currentUrlParams = new URLSearchParams(location.search);
        let hasDefaultToApply = false;

        const defaultFilterKeys: (keyof TFilterOptions['defaults'])[] = [
            'interview_status',
            'is_active',
            'is_archived',
            'is_published',
        ];

        defaultFilterKeys.forEach(filterKey => {
            if (
                !currentUrlParams.has(filterKey) &&
                filterOptions.defaults[filterKey] !== undefined
            ) {
                currentUrlParams.set(
                    filterKey,
                    String(filterOptions.defaults[filterKey]),
                );
                hasDefaultToApply = true;
            }
        });

        const defaultServiceKey = filterOptions.defaults.service_key;
        if (!currentUrlParams.has('service_key') && defaultServiceKey) {
            currentUrlParams.set('service_key', String(defaultServiceKey));
            hasDefaultToApply = true;
        }

        const defaultOrdering = filterOptions.defaults.ordering;
        if (!currentUrlParams.has('ordering') && defaultOrdering) {
            currentUrlParams.set('ordering', String(defaultOrdering));
            hasDefaultToApply = true;
        }

        if (hasDefaultToApply) {
            history.replace(
                `${location.pathname}?${currentUrlParams.toString()}`,
            );
        }
    }, [filterOptions, location.pathname, location.search]);

    const onPageChange = useCallback(
        (pageIndex: number) => {
            const currentUrlParams = new URLSearchParams(location.search);
            if (pageIndex >= 1) {
                currentUrlParams.set('page', String(pageIndex + 1));
            } else {
                currentUrlParams.delete('page');
            }
            history.push(`${location.pathname}?${currentUrlParams.toString()}`);
            setCurrentPage(pageIndex);
        },
        [location.pathname, location.search],
    );

    const onSortedChange = useCallback(
        (newSorted: { id: string; desc: boolean }[]) => {
            didTouchDefaultFilters.current = true;
            const currentUrlParams = new URLSearchParams(location.search);

            const primarySort = newSorted[0];
            if (primarySort && allowedSortColumns.has(primarySort.id)) {
                currentUrlParams.set(
                    'ordering',
                    `${primarySort.desc ? '-' : ''}${primarySort.id}`,
                );
                currentUrlParams.set('page', '1');
                setCurrentPage(0);
                history.push(
                    `${location.pathname}?${currentUrlParams.toString()}`,
                );
            } else if (!primarySort) {
                currentUrlParams.delete('ordering');
                currentUrlParams.set('page', '1');
                setCurrentPage(0);
                history.push(
                    `${location.pathname}?${currentUrlParams.toString()}`,
                );
            }
        },
        [allowedSortColumns, location.pathname, location.search],
    );

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    useEffect(() => {
        applyDefaultFilters();
    }, [applyDefaultFilters]);

    useEffect(() => {
        if (!filterOptionsLoaded) {
            return undefined;
        }

        const controller = new AbortController();
        const params = new URLSearchParams(location.search);
        const pageParam = Number(params.get('page'));
        const pageIndex = pageParam && pageParam > 0 ? pageParam - 1 : 0;
        setCurrentPage(pageIndex);
        fetchWorkers(pageIndex, params, controller.signal);
        return () => controller.abort();
    }, [fetchWorkers, filterOptionsLoaded, location.search]);

    const pages = meta?.count ? Math.ceil(meta.count / PAGE_SIZE) : -1;
    const showPaginationBottom = (meta?.count || 0) > PAGE_SIZE;
    const currentUrlParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );
    const defaultSorted = useMemo(
        () => {
            const ordering = currentUrlParams.get('ordering');
            if (!ordering) {
                return [];
            }

            const desc = ordering.startsWith('-');
            const id = desc ? ordering.substring(1) : ordering;
            return [{ id, desc }];
        },
        [currentUrlParams],
    );
    const reloadData = useCallback(() => {
        fetchWorkers(currentPage, new URLSearchParams(currentUrlParams));
    }, [currentPage, fetchWorkers, currentUrlParams]);

    return {
        content,
        pages,
        showPaginationBottom,
        loading,
        currentPage,
        filterOptionValuesToDisplay,
        allowedSortColumns,
        currentUrlParams,
        defaultSorted,
        onFilterChange,
        onPageChange,
        onSortedChange,
        fetchWorkers,
        reloadData,
        filterOptionsLoaded,
    };
};
